import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"
import { materialRulesQueryOptions } from "@/features/bill-of-materials/hooks/useMaterialRules"
import { shelfCodesQueryOptions } from "@/features/bill-of-materials/hooks/useShelfCodes"
import { CalculationFlow } from "@/features/list-of-materials/components/how-it-works/CalculationFlow"
import { ExampleMeter } from "@/features/list-of-materials/components/how-it-works/ExampleMeter"
import { HowSection } from "@/features/list-of-materials/components/how-it-works/HowSection"
import { MissingTable } from "@/features/list-of-materials/components/how-it-works/MissingTable"
import { PageHead, SectionTitle } from "@/features/list-of-materials/components/ui/PageHead"
import { pickExampleMeter } from "@/features/list-of-materials/core/howItWorks"
import { toShelfCodeSummary } from "@/features/list-of-materials/core/shelfCodeRows"
import {
  pickableArticlesQueryOptions,
  lomCodingQueryOptions,
} from "@/features/list-of-materials/hooks/useLomData"
import {
  lomVersionsQueryOptions,
  useSelectedLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"

export const Route = createFileRoute("/(authenticated)/list-of-materials/how-it-works")({
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, deps }) => {
    const { selected } = await context.queryClient.ensureQueryData(
      lomVersionsQueryOptions(deps.version),
    )
    if (!selected) return
    await Promise.all([
      context.queryClient.ensureQueryData(lomCodingQueryOptions(selected.id)),
      context.queryClient.ensureQueryData(shelfCodesQueryOptions(selected.id)),
      context.queryClient.ensureQueryData(materialRulesQueryOptions(selected.id)),
    ])
    void context.queryClient.prefetchQuery(pickableArticlesQueryOptions(selected.id))
  },
  component: HowItWorksPage,
})

function HowItWorksPage() {
  const { version } = useSelectedLomVersion()
  const { data: coding } = useSuspenseQuery(lomCodingQueryOptions(version.id))
  const { data: shelfCodes } = useSuspenseQuery(shelfCodesQueryOptions(version.id))
  const { data: rules } = useSuspenseQuery(materialRulesQueryOptions(version.id))
  const { data: articles } = useQuery(pickableArticlesQueryOptions(version.id))

  const example = useMemo(() => {
    const describe = (nr: string) => articles?.find((a) => a.articleNumber === nr)?.descriptions[0]
    return pickExampleMeter(
      coding.groups,
      coding.templates,
      shelfCodes.map(toShelfCodeSummary),
      rules,
      describe,
    )
  }, [coding, shelfCodes, rules, articles])

  return (
    <>
      <PageHead
        title={<Trans>How it works</Trans>}
        sub={
          <Trans>
            How a store's blueprint becomes its list of materials, meter by meter, and what each
            page of these settings adds to it.
          </Trans>
        }
      />

      <SectionTitle>{t`The calculation`}</SectionTitle>
      <CalculationFlow />
      <p className="text-lom-ink-2 mt-3 mb-10 max-w-4xl text-[15px]">
        <Trans>
          Steps marked as not calculated yet can already be filled in and are shown on these pages,
          but store lists don't use them yet. Each step below says what the calculation does until
          then.
        </Trans>
      </p>

      <SectionTitle>{t`One meter, followed through`}</SectionTitle>
      <div className="mb-4">
        <ExampleMeter example={example} />
      </div>

      <HowSection topic="blueprint" title={t`The blueprint`}>
        <p>
          <Trans>
            Every store is calculated from its latest blueprint. The blueprint says how many meters
            each merchandise group has, <b>on the gondola</b> and <b>on the wall</b>, and which of
            them are 1.25 m (gondola or wall), Small Bottom Shelf (gondola) or Low Wall (wall). A
            setting a shelf's kind can't have, such as one left over after changing a gondola into a
            wall, doesn't count.
          </Trans>
        </p>
        <p>
          <Trans>
            Every meter gets a number within its group: the number written on the drawing, or else
            the store's counting rules. A gondola counts from the tills, and FLOWER, LINNEN,
            ELECTRA, HAIR and BED from the end of their gondola. A group that stands only on the
            wall counts along the wall from PROMO: the walls in the order they are walked from the
            entrance, each wall in walking direction, the same walk the bulk updates use. A group
            spread over several blocks without numbers is counted as one sequence, so every number
            occurs exactly once; the list shows a warning for that group. One number written over
            several touching blocks of a group on the same row counts for all of them. On the wall
            it may also run around a corner: TOYS "1-12+17" drawn once over 13 wall meters is read
            over all 13, in the direction the text runs. Numbers on the drawing that don't add up,
            for example a range that reaches from the wall onto a gondola, are set aside the same
            way, with a warning.
          </Trans>
        </p>
      </HowSection>

      <HowSection
        topic="coding"
        title={t`Coding`}
        page={{ href: "/list-of-materials/coding", label: t`Coding` }}
      >
        <p>
          <Trans>
            A meter finds its shelf code in the coding of its own side: the table for the group's
            total meters, in the column of its number. LADIES with 7 meters, W 1, 2, 3, 5 and G 4,
            6, 7: meter 5 reads the wall table for 7 m, column M5, and meter 4 the gondola table for
            7 m, column M4.
          </Trans>
        </p>
        <p>
          <Trans>
            A cell can add a second code, like <b>A6+J</b>: the meter then gets the parts of both
            codes. A side needs a table for exactly the group's size; without one, the meters get no
            parts and the list names what is missing. A meter number that table doesn't have gets no
            parts either, with a warning.
          </Trans>
        </p>
      </HowSection>

      <HowSection
        topic="shelf-codes"
        title={t`Shelf codes`}
        page={{ href: "/list-of-materials/shelf-codes", label: t`Shelf codes` }}
      >
        <p>
          <Trans>
            A shelf code is one meter of racking, as a page of the Racking Guidelines: its height,
            its shelves and <b>its parts per meter</b>. Every meter with that code adds these parts
            to the store's list. Changing a code changes the list of every store that uses it.
          </Trans>
        </p>
        <p>
          <Trans>
            Every code has <b>its own colour</b>, as the guidelines print it, set under Edit. It is
            the colour of the code everywhere: the square in front of it, the floor plan and the
            materials report. A new code starts with its family's colour, a variant with its code's.
            The shelving configuration, what sits at each notch, only describes the code and is
            changed under Edit too.
          </Trans>
        </p>
        <p>
          <Trans>
            A code can have <b>setup variants</b>: the full parts of a meter of that code with 1.25
            m, Small Bottom Shelf or Low Wall, or 1.25 m with one of those, such as B4-125 for B4 at
            1.25 m. A meter with those setups takes the variant's parts instead of going through the
            rules for them. On the Shelf codes page, <b>Download shelf codes (xlsx)</b> gives every
            code, variant and part in one workbook to review and extend; the upload shows per code
            what it adds, changes and removes before saving, and only a Remove column removes.
          </Trans>
        </p>
      </HowSection>

      <HowSection
        topic="rules"
        title={t`Rules`}
        page={{ href: "/list-of-materials/rules", label: t`Rules` }}
      >
        <p>
          <Trans>
            Rules change a meter's parts when the meter has a setup: <b>1.25 m</b>,{" "}
            <b>Small Bottom Shelf</b> or <b>Low Wall</b>. A rule replaces a part, leaves it out,
            adds one or sets its amount, and can hold for one side, shelf code or group only.
          </Trans>
        </p>
        <p>
          <Trans>
            <b>Which stores.</b> Rules, the parts per fixture and the other amounts of an extra all
            take the same store conditions: a store version (1.0 creme or 2.0 white), a country or
            one region of it (such as BE-WA), and store sizes. Left empty, a condition holds for
            every store. Where several match a store, the one with the most conditions filled in
            wins, so a region's amount beats its country's. Preview lists, per store, what its
            conditions leave out and why.
          </Trans>
        </p>
        <p>
          <Trans>
            Rules run in steps: first the rules for every meter, then Small Bottom Shelf, then Low
            Wall, then 1.25 m. A meter that is both Small Bottom Shelf and 1.25 m goes through both
            steps. When two rules in a step are about the same part, the one with the most
            conditions wins.
          </Trans>
        </p>
        <p>
          <Trans>
            <b>Keep as is</b> records that a part stays the same with a setup. To fill in lists, per
            setup, the parts that don't have a decision for every shelf code yet: a decision for all
            codes, or one for the code itself. Until then, the meter keeps the standard part and the
            list shows one warning per part. Everything still open can also be downloaded as one
            Excel workbook, filled in outside Shelfi and uploaded again; the upload shows what every
            row changes before it saves.
          </Trans>
        </p>
        <p>
          <Trans>
            <b>Count stores affected</b> on To fill in runs the list of every store with an approved
            blueprint and shows, per open part and per setup, how many stores and meters still keep
            the standard part, so the parts that matter most can be decided first. It is the same
            calculation as a store's preview, and the count stays until it is run again. An article
            only creme (1.0) stores get is an add rule with store version 1.0; To fill in lists them
            and adds one with <b>Add article for 1.0 stores</b>.
          </Trans>
        </p>
        <p>
          <Trans>
            1.25 m meters work the same way: their own shelf code's parts, changed by the 1.25 m
            rules. The old fixed list, one of each of 9 articles for every 1.25 m meter, is no
            longer used.
          </Trans>
        </p>
        <p>
          <Trans>
            A shelf code can also have a <b>setup variant</b>: a code of its own for, say, B4 at
            1.25 m. A meter of B4 at 1.25 m then takes the variant's parts instead, and the 1.25 m
            rules are skipped for it; its other setups still go through the rules. When two variants
            fit a meter equally well, it gets no parts and the list says which variant to add.
          </Trans>
        </p>
      </HowSection>

      <HowSection topic="per-run" title={t`Per run`}>
        <p>
          <Trans>
            Parts such as uprights and feet sit between meters, so a run needs one more than it has
            meters: a run of 10 meters needs 11. A run is one block of a group on one row. Articles
            marked <b>extra per run</b> are counted once more for every run, taken from the run's
            last meter after its rules, so a run that ends on a Low Wall meter gets that meter's
            upright. Rules counted <b>once per run</b> then apply to what the run adds.
          </Trans>
        </p>
        <p>
          <Trans>
            Which articles count once more is set per article in the version's article list (extra
            per run). An article nobody has decided on yet counts as no extra, so until Action marks
            the uprights and feet, a run of 10 meters gets 10.
          </Trans>
        </p>
      </HowSection>

      <HowSection
        topic="extras"
        title={t`Extras`}
        page={{ href: "/list-of-materials/extras", label: t`Extras` }}
      >
        <p>
          <Trans>
            Besides its racking, a store needs extras. <b>Per store</b>: an amount for every store,
            with other amounts for some countries, regions (such as BE-WA) or store sizes, and only
            for 1.0 or 2.0 stores when set. The amount can follow the store: per racking meter, 1.25
            m meter, meter with a Small Bottom Shelf or Low Wall meter, or per fixture of a kind on
            the blueprint (such as 3 clips per 1.25 m meter), or per checkout, trolley, customer
            basket, shopping cart or another count of the store information, for things that are not
            drawn on the blueprint. It can also be given once, when the store has at least one (10
            clips if the store has any 1.25 m meter). The meters are the ones the list is worked out
            for, so they match the rest of the list. <b>Per fixture</b>: the articles of a fixture,
            plus the parts of the shelf codes linked to it, counted for every fixture of that kind
            on the blueprint. An article of a fixture can hold for some stores only, and have
            another amount for others, such as 3 drip trays per display in the Netherlands and 2
            elsewhere. A linked shelf code can hold for some stores too. Links add up, so per
            fixture the matching links with the most conditions filled in all count and the others
            don't: with End cap → * for every store and → *BE for Belgium, a Belgian store gets only
            the parts of *BE.
          </Trans>
        </p>
        <p>
          <Trans>
            End caps count from the blueprint, each size as its own kind: 100×70, 125×70, 100×50 and
            125×50, so a wide or shallow end cap can get other parts. Promo and Sustainability
            gondola ends are kinds of their own.
          </Trans>
        </p>
        <p>
          <Trans>
            The store information is what the Store information step of a new project shows and
            saves: one set per store, the newest saved version counts. The store size comes from its
            Total racks, with the sizes under Settings → Store Sizes.
          </Trans>
        </p>
        <p>
          <Trans>
            Fixtures always count from the blueprint: what is placed. The store information also has
            fixture counts, such as Gondola Ends (GE), pallets, displays, baskets and batteries.
            Where one is filled in and differs from the blueprint, the list shows a warning with
            both numbers and keeps the blueprint's. GE is held against every end cap, Promo and
            Sustainability included.
          </Trans>
        </p>
      </HowSection>

      <HowSection
        topic="articles"
        title={t`Articles`}
        page={{ href: "/list-of-materials/articles", label: t`Articles` }}
      >
        <p>
          <Trans>
            Every version has one article list: each article once, with one description, what a
            creme (1.0) store orders instead, and whether a run needs one extra. Shelf codes, rules
            and extras pick their articles from it, so a number is typed once and its description is
            the same everywhere. The list, the preview and the downloads name a part by this
            description; a shelf code that still has an older text shows it under the description.
          </Trans>
        </p>
        <p>
          <Trans>
            An article the settings above use but the list doesn't have shows up as missing, until
            someone adds it. Where its description differs between shelf codes, the planner picks
            one.
          </Trans>
        </p>
        <p>
          <Trans>
            For a creme (1.0) store, the last step gives every line its creme number: the list's
            creme number, or the same number when it is the same in both. A part whose creme number
            is still to fill in, or one the list doesn't have, shows as 000000000 with its white
            number next to it, and the list warns. The amounts don't change: two such parts stay two
            lines. A store without a version gets the white numbers, with a warning.
          </Trans>
        </p>
      </HowSection>

      <HowSection
        topic="preview"
        title={t`List per store`}
        page={{ href: "/list-of-materials/preview", label: t`Preview` }}
      >
        <p>
          <Trans>
            Preview calculates one store with the version you are looking at: every meter with its
            code and parts, the totals, and the warnings. The list can be exported to Excel from
            there.
          </Trans>
        </p>
        <p>
          <Trans>
            The <b>Floor plan</b> tab draws the store's blueprint. <b>Numbering</b> shows every
            group in its blueprint colour with a label per block, such as DECO 12–4: the numbers as
            they read on the plan, left to right or top to bottom. Zoom in for the number of every
            meter, in a grey box when it is drawn on the blueprint, plain when it is counted; closer
            in, the code shows under it. <b>Shelf codes</b> shows the code of every meter in its own
            colour, with a dot where the number is drawn. Hovering a meter or picking a group under{" "}
            <b>Show group</b> lights up all its blocks. Click a meter to see why it has its code:
            its group, side and number, where the number came from, the coding table it read, its
            setups and the rules that changed it. A red outline has no code; a dashed one belongs to
            a group whose numbering has a warning.
          </Trans>
        </p>
        <p>
          <Trans>
            <b>Try another POS</b> there lets you drag the tills elsewhere: the gondolas are
            numbered and coded again, the meters that get another code turn yellow, and the
            materials show what it would need more or less. Nothing is saved. Meters whose numbers
            are drawn on the blueprint don't move along, so a store with badges changes less.
          </Trans>
        </p>
      </HowSection>

      <HowSection topic="missing" title={t`When something is missing`}>
        <p>
          <Trans>
            The list is still made when a setting is missing. What it does instead, and where to add
            what's missing:
          </Trans>
        </p>
        <MissingTable />
      </HowSection>

      <HowSection topic="versions" title={t`Versions`}>
        <p>
          <Trans>
            All these settings belong to a configuration version; the switch at the bottom of the
            menu chooses which one you see and change. Preview uses that version; the materials
            report uses the active version. Copying a version copies all its list of materials
            settings.
          </Trans>
        </p>
        <p>
          <Trans>
            Before a draft version goes live, check it over all stores: on Preview,{" "}
            <b>Download all stores (xlsx)</b> calculates every store with the version you are
            looking at, and with
            <b> Next to the active version</b> also with the active one. The file shows per article
            both totals and the difference, per store the articles that change, and the stores that
            couldn't be calculated and why.
          </Trans>
        </p>
      </HowSection>
    </>
  )
}
